// src/components/ContactForm.tsx
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import NotesIcon from "@mui/icons-material/Notes";
import { formatPhoneNumber } from "../utils/stringUtils";
import { NotesField } from "../services/bookingService";

type Props = {
  name: string;
  phone: string;
  email: string;
  address: string;
  appointmentNotes: string;
  notesField?: NotesField | null;
  validateAll?: boolean;
  onChange: (field: string, value: string) => void;
};

const MAX_NOTES = 300;

export default function ContactForm({
  name,
  phone,
  email,
  address,
  appointmentNotes,
  notesField,
  validateAll,
  onChange,
}: Props) {
  const theme = useTheme();
  const notesCount = appointmentNotes?.length ?? 0;
  
  // Track touched fields for validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  // When validateAll is true, mark all fields as touched
  React.useEffect(() => {
    if (validateAll) {
      setTouched({
        name: true,
        phone: true,
        email: true,
        address: true,
        appointmentNotes: true,
      });
    }
  }, [validateAll]);
  
  // Helper function to mark all other fields as touched when a field is focused
  const handleFieldFocus = (currentField: string) => {
    setTouched(prev => {
      const allFields = ['name', 'phone', 'email', 'address', 'appointmentNotes'];
      const updated = { ...prev };
      allFields.forEach(field => {
        if (field !== currentField) {
          updated[field] = true;
        }
      });
      return updated;
    });
  };
  
  // Determine notes field configuration
  // If notesField is an object, use custom config; otherwise use default (old layout)
  const hasCustomNotesField = notesField !== null && notesField !== undefined;
  const notesLabel = hasCustomNotesField ? notesField.title : "Notes (optional)";
  const notesPlaceholder = hasCustomNotesField ? notesField.description : "Gate code, pets, parking info, etc.";
  const notesRequired = hasCustomNotesField ? notesField.required : false;

  // Validation logic
  const nameEmpty = !name.trim();
  const nameTooShort = name.trim().length > 0 && name.trim().length < 2;
  const nameError = touched.name && (nameEmpty || nameTooShort);
  
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneEmpty = !phoneDigits;
  const phoneInvalid = phoneDigits.length > 0 && phoneDigits.length < 10;
  const phoneError = touched.phone && (phoneEmpty || phoneInvalid);
  
  const emailEmpty = !email.trim();
  const emailInvalid = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const emailError = touched.email && (emailEmpty || emailInvalid);
  
  const addressEmpty = !address.trim();
  const addressError = touched.address && addressEmpty;
  
  const notesEmpty = !appointmentNotes.trim();
  const notesError = notesRequired && touched.appointmentNotes && notesEmpty;

  const nearLimit = MAX_NOTES - notesCount <= 25;

  const fieldSX = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      transition: "box-shadow .15s ease, border-color .15s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,
      },
    },
  } as const;

  return (
    <Box mt={4}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: theme.palette.divider,
          backdropFilter: "blur(4px)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
            spacing={2}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Name</strong> & <strong>Phone</strong> required
            </Typography>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TextField
              fullWidth
              required
              label="Full Name"
              value={name}
              onChange={(e) => onChange("name", e.target.value)}
              onFocus={() => handleFieldFocus("name")}
              onBlur={() => setTouched(prev => ({ ...prev, name: true }))}
              autoComplete="name"
              placeholder="First and last name"
              error={nameError}
              helperText={nameError ? (nameEmpty ? "This field is required." : "Please enter at least 2 characters.") : " "}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
                inputProps: { autoCapitalize: "words" as any },
              }}
              sx={fieldSX}
            />

            <TextField
              fullWidth
              required
              label="Phone Number"
              value={phone}
              onChange={(e) => onChange("phone", formatPhoneNumber(e.target.value))}
              onFocus={() => handleFieldFocus("phone")}
              onBlur={() => setTouched(prev => ({ ...prev, phone: true }))}
              autoComplete="tel"
              placeholder="(555) 555-5555"
              error={phoneError}
              helperText={phoneError ? (phoneEmpty ? "This field is required." : "Enter a 10-digit number.") : "We'll text you appointment updates."}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" />
                  </InputAdornment>
                ),
                inputMode: "tel",
              }}
              sx={fieldSX}
            />

            <TextField
              fullWidth
              required
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => onChange("email", e.target.value)}
              onFocus={() => handleFieldFocus("email")}
              onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
              autoComplete="email"
              placeholder="you@example.com"
              error={emailError}
              helperText={emailError ? (emailEmpty ? "This field is required." : "Enter a valid email address.") : " "}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" />
                  </InputAdornment>
                ),
                inputMode: "email",
              }}
              inputProps={{ autoCapitalize: "none", spellCheck: "false" as any }}
              sx={fieldSX}
            />

            <TextField
              fullWidth
              required
              label="Street Address"
              value={address}
              onChange={(e) => onChange("address", e.target.value)}
              onFocus={() => handleFieldFocus("address")}
              onBlur={() => setTouched(prev => ({ ...prev, address: true }))}
              autoComplete="street-address"
              placeholder="123 Main St"
              error={addressError}
              helperText={addressError ? "This field is required." : " "}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={fieldSX}
            />

            {/* Notes spans full width */}
            <TextField
              fullWidth
              required={notesRequired}
              label={notesLabel}
              value={appointmentNotes}
              onChange={(e) => onChange("appointmentNotes", e.target.value)}
              onFocus={() => handleFieldFocus("appointmentNotes")}
              onBlur={() => setTouched(prev => ({ ...prev, appointmentNotes: true }))}
              placeholder={notesPlaceholder}
              multiline
              minRows={3}
              inputProps={{ maxLength: MAX_NOTES }}
              error={notesError}
              helperText={
                <Box
                  component="span"
                  sx={{
                    color: notesError ? "error.main" : (nearLimit ? "warning.main" : "text.secondary"),
                  }}
                  aria-live="polite"
                >
                  {notesError && (
                    <Box component="span" sx={{ display: "block", mb: 0.5 }}>
                      This field is required.
                    </Box>
                  )}
                  {/* {notesDescription && !notesError && (
                    <Box component="span" sx={{ display: "block", mb: 0.5 }}>
                      {notesDescription}
                    </Box>
                  )} */}
                  {notesCount}/{MAX_NOTES}
                </Box>
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NotesIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ gridColumn: "1 / -1", ...fieldSX }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
